import React, { useEffect, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { useDispatch, useSelector } from 'react-redux';
import { dailyUpdateByLoginIdAsQuarter } from '../../Redux/DailyUpdate/Action';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { getLeaveBalanceList } from "../../Redux/Leave/Action";
import { format } from 'date-fns';


ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const getCurrentQuarterDetails = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0-based

    const quarter = Math.floor(month / 3) + 1;
    const startMonth = (quarter - 1) * 3;

    const startDate = new Date(year, startMonth, 1);
    const endDate = new Date(year, startMonth + 3, 0);

    return {
        quarter: `Q${quarter}`,
        startDate,
        endDate
    };
};

const SupportDash = () => {

    const dispatch = useDispatch();
    const { allTaskDetails } = useSelector((store) => store.task);
    const { dailyUpdatebyIdPerQuarter } = useSelector((store) => store.dailyUpdate);
    const { leavebalancelist } = useSelector((store) => store.leave);
    const token = localStorage.getItem("token");
    const loginId = localStorage.getItem("loginId");

    const [statusCounts, setStatusCounts] = useState({});
    const [quarterInfo, setQuarterInfo] = useState(null);

    const loadDashBoard = (token, loginId) => {
        const data = getCurrentQuarterDetails();
        setQuarterInfo(data);

        dispatch(getLeaveBalanceList(token, loginId, 'Apply'));
        dispatch(dailyUpdateByLoginIdAsQuarter(loginId, token, format(data.startDate, "yyyy-MM-dd"), format(data.endDate, "yyyy-MM-dd")));
    }

    useEffect(() => {
        loadDashBoard(token, loginId);
    }, [token, loginId]);

    useEffect(() => {
        if (allTaskDetails?.taskMaster?.length > 0) {
            const counts = allTaskDetails.taskMaster.reduce((acc, task) => {
                const status = task.status || "Unknown";
                acc[status] = (acc[status] || 0) + 1;
                return acc;
            }, {});
            setStatusCounts(counts);
        } else {
            setStatusCounts({});
        }
    }, [allTaskDetails]);

    useEffect(() => {
        const data = getCurrentQuarterDetails();
        setQuarterInfo(data);
    }, []);

    const projects = dailyUpdatebyIdPerQuarter?.projects ?? [];

    const COLORS = [
        "#4CAF50",
        "#2196F3",
        "#FF9800",
        "#9C27B0",
        "#F44336",
        "#00BCD4"
    ];

    const data = {
        labels: projects.map(p => p.projectCode),
        datasets: [
            {
                label: "Project Count",
                data: projects.map(p => p.count),
                backgroundColor: projects.map(
                    (_, index) => COLORS[index % COLORS.length]
                ),
                borderColor: "#fff",
                borderWidth: 2
            }
        ]
    };

    const options = {
        responsive: true,
        plugins: {

            legend: {
                display: false
            },

            tooltip: {
                callbacks: {
                    label: (context) => {
                        const label = context.label || "";
                        const value = context.raw || 0;

                        const total = context.dataset.data.reduce(
                            (sum, v) => sum + v,
                            0
                        );

                        const percentage =
                            total > 0
                                ? ((value / total) * 100).toFixed(1)
                                : 0;

                        return `${label}: ${value} (${percentage}%)`;
                    }
                }
            },


            datalabels: {
                color: "#fff",
                font: {
                    size: 13,
                    weight: "bold"
                },
                formatter: (value) => (value === 0 ? "" : value)
            }

        }
    };



    const inTimeBuckets = dailyUpdatebyIdPerQuarter?.inTimeBuckets ?? [];
    const outTimeBuckets = dailyUpdatebyIdPerQuarter?.outTimeBuckets ?? [];

    // helper to get count by label
    const getCount = (list, label) =>
        list.find(item => item.label === label)?.count ?? 0;

    const pieData = [
        { label: "08 AM - 11 AM", count: getCount(inTimeBuckets, "08 AM - 11 AM") },
        { label: "11 AM - 02 PM", count: getCount(inTimeBuckets, "11 AM - 02 PM") },
        { label: "02 PM - 05 PM", count: getCount(outTimeBuckets, "14 PM - 17 PM") },
        { label: "05 PM - 08 PM", count: getCount(outTimeBuckets, "17 PM - 20 PM") }
    ];

    const TIME_COLORS = [
        "#4CAF50", // In (early)
        "#2196F3", // In (late)
        "#FF9800", // Out (early)
        "#F44336"  // Out (late)
    ];

    const timeData = {
        labels: pieData.map(p => p.label),
        datasets: [
            {
                data: pieData.map(p => p.count),
                backgroundColor: TIME_COLORS,
                borderColor: "#fff",
                borderWidth: 2
            }
        ]
    };

    const timeOptions = {
        responsive: true,
        plugins: {

            legend: {
                display: false
            },

            datalabels: {
                color: "#fff",
                font: {
                    size: 13,
                    weight: "bold"
                },
                formatter: (value) => (value === 0 ? "" : value)
            },

            tooltip: {
                callbacks: {
                    label: (context) => `${context.raw}`
                }
            }
        }
    };



    return (
        <div>

            <div className='row'>
                <div className="col-md-12 mb-4">

                    <div className="row">
                        {/* Chart 1 */}
                        <div className="col-lg-4 col-md-6 col-12 mb-4">
                            <div className="card shadow-sm rounded-4" style={{ minHeight: "409px" }}>
                                <div className="card-body d-flex flex-column justify-content-center align-items-center">
                                    <h6 className="text-center mb-3 w-100 fw-bold">
                                        {`In/Out Time Overview (${quarterInfo?.quarter})`}
                                    </h6>

                                    <div
                                        style={{
                                            width: "100%",
                                            maxWidth: 420,
                                            height: 280,
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                        }}
                                    >
                                        {pieData.some(p => p.count > 0) ? (
                                            <Pie data={timeData} options={timeOptions} />
                                        ) : (
                                            <p>No data available</p>
                                        )}
                                    </div>

                                    {/* legend */}
                                    <div
                                        style={{
                                            marginTop: 12,
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            columnGap: "18px",
                                            flexWrap: "wrap"
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                            <span style={{ fontSize: 13, fontWeight: 600 }}>In Time</span>

                                            {pieData.slice(0, 2).map((p, idx) => (
                                                <div key={idx} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                    <div style={{ width: 12, height: 12, backgroundColor: TIME_COLORS[idx], borderRadius: 2 }} />
                                                    <span style={{ fontSize: 12, color: "#555" }}>{p.label}</span>
                                                </div>
                                            ))}
                                        </div>

                                        <div style={{ width: 1, height: 16, backgroundColor: "#ccc" }} />

                                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                            <span style={{ fontSize: 13, fontWeight: 600 }}>Out Time</span>

                                            {pieData.slice(2, 4).map((p, idx) => (
                                                <div key={idx} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                    <div style={{ width: 12, height: 12, backgroundColor: TIME_COLORS[idx + 2], borderRadius: 2 }} />
                                                    <span style={{ fontSize: 12, color: "#555" }}>{p.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* Chart 2 */}
                        <div className="col-lg-4 col-md-6 col-12 mb-4">
                            <div className="card shadow-sm rounded-4" style={{ minHeight: "409px" }}>
                                <div className="card-body d-flex flex-column justify-content-center align-items-center">
                                    <h6 className="text-center mb-3 w-100 fw-bold">
                                        {`Daily Update Overview (${quarterInfo?.quarter})`}
                                    </h6>

                                    <div
                                        style={{
                                            width: "100%",
                                            maxWidth: 420,
                                            height: 280,
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                        }}
                                    >
                                        {data.labels.length > 0 ? (
                                            <Pie data={data} options={options} />
                                        ) : (
                                            <p>No data available</p>
                                        )}
                                    </div>

                                    <div
                                        style={{
                                            marginTop: 12,
                                            display: "flex",
                                            justifyContent: "center",
                                            gap: "16px",
                                            flexWrap: "wrap"
                                        }}
                                    >
                                        {data.labels.length > 0 && (
                                            projects.map((p, index) => (
                                                <div key={p.projectCode} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                    <div
                                                        style={{
                                                            width: 12,
                                                            height: 12,
                                                            backgroundColor: COLORS[index % COLORS.length],
                                                            borderRadius: 2
                                                        }}
                                                    />
                                                    <span style={{ fontSize: 12 }}>
                                                        {p.projectCode} ({p.count})
                                                    </span>
                                                </div>
                                            ))
                                        )}
                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* Chart 3 */}
                        <div className="col-lg-4 col-md-12 col-12 mb-4">
                            <div className="card shadow-sm rounded-4" style={{ minHeight: "409px" }}>
                                <div className="card-body d-flex flex-column">
                                    <h6 className="text-center mb-3 fw-bold">Leave Data</h6>

                                    <div style={{ width: "100%", height: 300, overflowY: "auto", padding: "0 10px" }}>
                                        {Array.isArray(leavebalancelist) && leavebalancelist.length > 0 ? (
                                            leavebalancelist.map((leave) => (
                                                <div
                                                    key={leave?.leaveBalanceId ?? "-"}
                                                    style={{
                                                        marginBottom: "12px",
                                                        padding: "8px",
                                                        borderRadius: "8px",
                                                        background: "#f1f5f9",
                                                        boxShadow: "inset 0 0 4px rgba(0,0,0,0.05)",
                                                    }}
                                                >
                                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", alignItems: "center" }}>
                                                        <span style={{ fontWeight: "600", color: "#0a6ebd", background: "#e0f0ff", padding: "2px 6px", borderRadius: "4px", fontSize: "0.85rem" }}>
                                                            {leave?.leaveCode ?? "-"}
                                                        </span>
                                                        <span style={{ fontSize: "0.85rem", color: "#333" }}>
                                                            Total Days: {(leave?.leaveDays ?? 0) + (leave?.appliedDays ?? 0)}
                                                        </span>
                                                    </div>

                                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.85rem", color: "#555" }}>
                                                        <span>Applied: {leave?.appliedDays ?? 0}</span>
                                                        <span>Available: {leave?.leaveDays ?? 0}</span>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-center text-muted">No leave data available</p>
                                        )}
                                    </div>

                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

        </div>
    );
};

export default SupportDash;